import {
  Box,
  Button,
  Flex,
  HStack,
  Heading,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalOverlay,
  Text,
  VStack,
  useColorModeValue,
} from '@chakra-ui/react';
import {motion} from 'framer-motion';
import {TablerCheck, TablerRefresh, TablerChevronRight} from '../../icons';
import {EHR_GREEN} from '../../constants/designTokens';
import {TablerModalCloseButton as ModalCloseButton} from '../common/TablerCloseButtons';

// Success surface shown after a telephone encounter is pushed to the EHR.
// Mirrors the clinical-note push success (green check, "sent to {EHR}" header,
// the reload-the-encounter nudge, terminal action) so the two flows feel like
// the same product. The primary action lands the registrar on the patient's
// history — the same place a note push returns them to.
const TelephoneEncounterPushSuccessModal = ({
  isOpen,
  onClose,
  onViewHistory,
  ehrBrandLabel = 'the EHR',
  patientName = '',
  primaryLabel = 'View patient history',
}: {
  isOpen: boolean;
  onClose: () => void;
  onViewHistory: () => void;
  ehrBrandLabel?: string;
  patientName?: string;
  // The terminal action's label. The planner keeps the default ("View patient
  // history"); embedded hosts label it for where they return the user (e.g.
  // charting's "Back to queue").
  primaryLabel?: string;
}) => {
  const subtleText = useColorModeValue('gray.600', 'gray.300');
  const strongText = useColorModeValue('gray.900', 'whiteAlpha.900');
  const nudgeBorder = useColorModeValue('gray.200', 'whiteAlpha.300');

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      isCentered
      size={{base: 'xs', sm: 'md'}}
    >
      <ModalOverlay bg="blackAlpha.600" />
      <ModalContent borderRadius="xl" mx={4} overflow="hidden">
        <ModalCloseButton borderRadius="full" />
        <ModalBody px={{base: 5, sm: 8}} pt={{base: 8, sm: 10}} pb={2}>
          <VStack spacing={4} align="center" textAlign="center">
            <motion.div
              initial={{scale: 0.6, opacity: 0}}
              animate={{scale: 1, opacity: 1}}
              transition={{type: 'spring', stiffness: 320, damping: 18}}
            >
              <Flex
                align="center"
                justify="center"
                w={{base: '56px', sm: '64px'}}
                h={{base: '56px', sm: '64px'}}
                borderRadius="full"
                bg={`${EHR_GREEN}1A`}
                color={EHR_GREEN}
              >
                <TablerCheck size={30} strokeWidth={3} aria-hidden="true" />
              </Flex>
            </motion.div>

            <VStack spacing={1.5} align="center">
              <Heading
                size="md"
                lineHeight="1.2"
                fontWeight="normal"
                color={strongText}
              >
                Telephone encounter sent to {ehrBrandLabel}
              </Heading>
              <Text fontSize="sm" color={subtleText} lineHeight="1.5">
                {patientName
                  ? `It's saved to ${patientName}'s chart and added to their history.`
                  : "It's saved to the patient's chart and added to their history."}
              </Text>
            </VStack>

            <HStack
              spacing={3}
              align="center"
              w="100%"
              bg={`${EHR_GREEN}12`}
              borderWidth="1px"
              borderColor={nudgeBorder}
              borderRadius="lg"
              px={{base: 3.5, sm: 4}}
              py={{base: 2.5, sm: 3}}
              textAlign="left"
            >
              <motion.div
                initial={{rotate: 0}}
                animate={{rotate: 360}}
                transition={{delay: 0.3, duration: 0.8, ease: 'easeOut'}}
                style={{display: 'flex'}}
              >
                <Flex
                  align="center"
                  justify="center"
                  w="30px"
                  h="30px"
                  borderRadius="full"
                  bg={`${EHR_GREEN}25`}
                  color={EHR_GREEN}
                  flexShrink={0}
                >
                  <TablerRefresh
                    size={16}
                    strokeWidth={2.5}
                    aria-hidden="true"
                  />
                </Flex>
              </motion.div>
              <Text fontSize="sm" color={strongText} lineHeight="1.4">
                <Text as="span" fontWeight="semibold">
                  Reload the {ehrBrandLabel} encounter page
                </Text>{' '}
                to see it there.
              </Text>
            </HStack>
          </VStack>
        </ModalBody>

        <ModalFooter
          px={{base: 5, sm: 8}}
          pb={{base: 5, sm: 6}}
          pt={4}
          flexDirection={{base: 'column-reverse', sm: 'row'}}
          gap={2}
        >
          <Button
            variant="ghost"
            colorScheme="gray"
            onClick={onClose}
            borderRadius="lg"
            size="lg"
            fontSize="md"
            fontWeight="normal"
            w={{base: '100%', sm: 'auto'}}
          >
            Stay here
          </Button>
          <Box flex={{sm: 1}} />
          <Button
            onClick={onViewHistory}
            borderRadius="lg"
            size="lg"
            fontSize="md"
            fontWeight="normal"
            w={{base: '100%', sm: 'auto'}}
            bg={EHR_GREEN}
            color="white"
            _hover={{bg: EHR_GREEN, filter: 'brightness(0.95)'}}
            _active={{bg: EHR_GREEN, filter: 'brightness(0.9)'}}
            rightIcon={<TablerChevronRight size={18} aria-hidden="true" />}
          >
            {primaryLabel}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default TelephoneEncounterPushSuccessModal;
